import OfferLabels from 'components/Common/Labels/OfferLabels'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import PerfectForGroupsLabel from 'components/Common/Labels/PerfectForGroupsLabel'
import Label from 'LuxKit/Label/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import OfferTilePricingUrgencyTags from 'components/OfferList/OfferListTiles/Support/OfferTilePricingUrgencyTags'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { useAppSelector } from 'hooks/reduxHooks'
import useChildrenStayFree from 'hooks/Offers/useChildrenStayFree'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { shouldShowPerfectForGroupsLabel } from 'lib/charterFlights/kidsFlyFreeUtils'
import TheresNoPlaceLikeHomeCampaignLabel from 'components/Common/Labels/TheresNoPlaceLikeHomeCampaignLabel'
import { useShowNoPlaceLikeHomeLabel } from 'hooks/useShowNoPlaceLikeHomeLabel'

interface Props {
  bestPackage?: App.HotelPackage
  cancellationPolicyType?: App.CancellationPolicyType
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  showUrgencyTags?: boolean
  timezoneOffset?: number
  urgencyTagVariant?: 'compact' | 'default'
}

function HotelSearchTileLabels({
  bestPackage,
  cancellationPolicyType,
  filters,
  offer,
  showUrgencyTags = true,
  timezoneOffset,
  urgencyTagVariant = 'default',
}: Props) {
  const intl = useIntl()
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const { fareClass, hasUserSelectedDates, isPromotionPriceApplied } = useMaldivesDirectBundleContext()
  const { shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({ charterBundleRoute, fareClass })
  const { totalPrice, totalMemberPrice, showMemberPrice } = useSearchPrices({
    offer,
    pkg: bestPackage,
    filters,
    useRealSearchPrices: true,
  })
  const saveAmount = totalPrice - (totalMemberPrice || 0)

  const { luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const searchTileLuxPlusInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])
  const showLuxPlusInclusions = !!searchTileLuxPlusInclusions.length
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const showMemberInclusionLabel = !!offer.luxPlus.hasMemberInclusions && !showLuxPlusInclusions && (!noGoogleAffiliations || isFlashHotelOffer(offer))
  const packages = useMemo(
    () => offer.packages ?? (bestPackage ? [bestPackage] : []),
    [offer.packages, bestPackage],
  )
  const { capacitiesByRate, includedGuestsByRate } = useMemo(() => ({
    capacitiesByRate: packages.map(pkg => pkg.roomRate?.capacities ?? []),
    includedGuestsByRate: packages.map(pkg => pkg.roomRate?.includedGuests ?? []),
  }), [packages])

  const { freeStayLabel } = useChildrenStayFree({
    offerType: offer.type,
    filters,
    capacitiesByRate,
    includedGuestsByRate,
  })
  const showPromotionSaleLabel = !!charterBundleRoute && hasUserSelectedDates && !!isPromotionPriceApplied
  const showTheresNoPlaceLikeHomeLabel = useShowNoPlaceLikeHomeLabel(offer.campaignTags)
  const promotionSaleLabel = showPromotionSaleLabel ? (
    <Label
      kind="primary"
      variant="urgency"
      startIcon={<SolidFireIcon />}
    >
      {intl.formatMessage({
        id: 'sW8RHHlJM1QGkzhbQFQd',
        defaultMessage: 'Limited Time Sale',
        description: 'Urgency label for the limited-time Maldives charter flight promotion modal',
      })}
    </Label>
  ) : null

  return <LabelGroup>
    {shouldShowKidsFlyFreeForOffer(offer, filters?.checkIn) && <KidsFlyFreeLabel />}
    {shouldShowPerfectForGroupsLabel(offer) && <PerfectForGroupsLabel />}
    <ContentTagLabels offerId={offer.id} page="search" />
    <OfferLabels
      offer={offer}
      urgencyLabels={urgencyTagVariant === 'compact' ? [] : undefined}
      saveAmount={showMemberPrice ? saveAmount : undefined}
      showMemberInclusionLabels={showMemberInclusionLabel}
      luxPlusInclusions={luxPlusInclusions}
      leadWithLuxPlusPrice
      freeStayLabel={freeStayLabel}
      suppressTheresNoPlaceLikeHomeCampaignLabel
    />
    {promotionSaleLabel}
    {showUrgencyTags && <OfferTilePricingUrgencyTags
      cancellationPolicyType={cancellationPolicyType}
      filters={filters}
      offer={offer}
      timezoneOffset={timezoneOffset}
      variant={urgencyTagVariant}
    />}
    {showTheresNoPlaceLikeHomeLabel && <TheresNoPlaceLikeHomeCampaignLabel />}
    {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
  </LabelGroup>
}

export default HotelSearchTileLabels
